import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo } from 'react';
import { useBookingConfig } from '../features/BookingConfiguration/hooks/useBookingConfig';
import { BookingConfig } from '../features/BookingConfiguration/types';
import { parseDaysInfoFromApi } from '../features/BookingConfiguration/utils';

interface BookingContextType {
  activeTab: number;
  setActiveTab: (tab: number) => void;
  smbId: string;
  setSmbId: (id: string) => void;
  config: BookingConfig | null;
  isNewConfig: boolean;
  isLoading: boolean;
  error: string | null;
  fetchConfig: () => Promise<void>;
  updateConfig: (payload: Partial<BookingConfig>) => Promise<boolean>;
}

const BookingContext = createContext<BookingContextType | undefined>(undefined);

export const BookingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [smbId, setSmbId] = useState('');
  
  const {
    config: rawConfig,
    isNewConfig, 
    isLoading, 
    error, 
    fetchConfig, 
    updateConfig 
  } = useBookingConfig(smbId);

  // Parse days_info.slots from API string[][] format to form TimeRange[] format
  // Type assertion needed because BookingConfig type uses API format but we store parsed format
  const config = useMemo(() => {
    if (!rawConfig?.days_info) return rawConfig;
    return {
      ...rawConfig,
      days_info: {
        slots: parseDaysInfoFromApi(rawConfig.days_info),
      },
    } as BookingConfig;
  }, [rawConfig]);

  // Automatically fetch config when smbId changes
  useEffect(() => {
    if (smbId) {
      fetchConfig();
    }
  }, [smbId, fetchConfig]);

  return (
    <BookingContext.Provider
      value={{
        activeTab,
        setActiveTab,
        smbId,
        setSmbId,
        config,
        isNewConfig,
        isLoading,
        error,
        fetchConfig,
        updateConfig
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

export const useBookingContext = () => {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error('useBookingContext must be used within a BookingProvider');
  }
  return context;
};
